// components/MarkCard.jsx
// Created: 2026-03-11 15:00:00 EST
// Updated: 2026-03-13 07:59:57 EDT
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.0.1
//
// Subtitle correction unit.
// Isolated draft state; features "Nudge" buttons for precise timecode adjustments.
// setMarks only called on blur to prevent app-wide re-renders.

import React from 'react';
import axios from 'axios';
import { Check, Flag, Lock, RotateCcw, Users, Wand2, PenLine, Trash2, Clock, Radio } from 'lucide-react';
import { Icon } from './Icon';
import { NUDGES } from '../constants';
import { Z } from '../constants/tokens';
import { getLanguageCode, getLanguageFontFamily, getLanguageMeta, isRightToLeftLanguage, normalizeLanguageKey } from '../constants/languages';
import { useConfirm } from '../confirmContext';
import { usePrompt } from '../promptContext';
import Button from './ui/Button';
import Badge from './ui/Badge';

const MarkCard = React.memo(function MarkCard({
  m,
  editingLanguage = '',
  visibleLanguages = [],
  sourceLanguage = '',
  vttSourceLanguage = '',
  theme,
  inputStyle,
  formatVTTTime,
  BACKEND_URL,
  videoRef,
  onRefresh,
  showPartLabel = false,
  isActive = false,
  // Compare overlay: the second engine's answer for this cue, and whether the
  // reader is currently looking at it instead of the primary.
  speakerCompareMode = false,
  editors = [],
  canEdit = true,
  rangeRestricted = false,
  selectedPart = null,
  onSwitchTape = () => {},
  userRole = '',
  projectRoot = '',
  currentUserId = null,
  speakerMap = {},
  onReassignSpeaker = null,
  cueWords = {},
  onReassignWord = null,
}) {
  const confirm = useConfirm();
  const prompt = usePrompt();
  const isVirtual = !m.id;  // unified block with no real mark yet
  // The cue's genuine original end. Marks carry timestamp_end; the start+2s
  // fallback is only for blocks that have no stored end. Used for the END
  // field's initial value, the "orig" hint and "Reset to original" so all
  // three agree — previously they all showed a fabricated start+2.
  const origEnd = m.timestamp_end ?? m.end ?? (m.timestamp_start + 2);
  const [draft,        setDraft]        = React.useState(m.edited_text || m.original_text || '');
  const [translationDrafts, setTranslationDrafts] = React.useState(() => ({ ...(m.translations || {}) }));
  const [tcOpen,       setTcOpen]       = React.useState(false);
  const [adjStart,     setAdjStart]     = React.useState(m.adjusted_start ?? m.timestamp_start);
  const [saveStatus,   setSaveStatus]   = React.useState(null);  // 'saving' | 'saved' | 'error'
  const [adjEnd,       setAdjEnd]       = React.useState(m.adjusted_end   ?? origEnd);
  const [activeNudge,  setActiveNudge]  = React.useState(0.5);
  const [tcSaving,     setTcSaving]     = React.useState(false);
  const [promoting,    setPromoting]    = React.useState(false);
  const [editTooltip,  setEditTooltip]  = React.useState(null); // { edits: [...], loading: bool }
  const editTooltipTimer = React.useRef(null);
  const dirtyRef       = React.useRef(false);  // true when user has unsaved local edits

  const showEditHistory = () => {
    if (!m.id || editTooltip?.edits) return; // already loaded
    setEditTooltip({ edits: [], loading: true });
    axios.get(`${BACKEND_URL}/edit-history`, { params: { record_id: String(m.id), table_name: 'transcript_marks', limit: 10 } })
      .then(r => setEditTooltip({ edits: r.data || [], loading: false }))
      .catch(() => setEditTooltip({ edits: [], loading: false }));
  };
  const hideEditHistory = () => {
    editTooltipTimer.current = setTimeout(() => setEditTooltip(null), 200);
  };
  const keepEditHistory = () => { clearTimeout(editTooltipTimer.current); };
  React.useEffect(() => () => clearTimeout(editTooltipTimer.current), []);

  // Sync if mark changes externally — but NOT if the user has a local dirty edit
  React.useEffect(() => {
    if (dirtyRef.current) return;  // user is editing — don't overwrite their work
    setDraft(m.edited_text || m.original_text || '');
    setTranslationDrafts({ ...(m.translations || {}) });
    setAdjStart(m.adjusted_start ?? m.timestamp_start);
    setAdjEnd(m.adjusted_end     ?? origEnd);
  }, [m.adjusted_end, m.adjusted_start, m.edited_text, m.original_text, m.timestamp_start, m.translations, origEnd]);

  const isAdjusted = m.adjusted_start != null;
  const sourceLanguageKey = normalizeLanguageKey(sourceLanguage || editingLanguage || 'Swedish');
  // The VTT file's actual language (what original_text/edited_text is written in)
  const rawVttLangKey = normalizeLanguageKey(vttSourceLanguage);
  // The VTT source language edits go to edited_text (via handleBlur),
  // other languages go to translations JSONB (via handleTranslationBlur)
  const vttLangKey = rawVttLangKey || sourceLanguageKey || '';

  // Auto-create a mark from a virtual unified block on first edit
  const promoteToMark = async () => {
    if (!isVirtual || promoting) return null;
    setPromoting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/mark`, {
        video_id: m.video_id,
        timestamp: m.timestamp_start,
        part: m.part_number,
      });
      return res.data?.id || res.data?.mark?.id;
    } catch (e) {
      console.error('[MarkCard] promote failed:', e);
      return null;
    } finally {
      setPromoting(false);
    }
  };

  const handleBlur = async () => {
    if (isVirtual) {
      setSaveStatus('saving');
      const newId = await promoteToMark();
      if (newId) {
        try {
          await axios.put(`${BACKEND_URL}/marks/${newId}/text?text=${encodeURIComponent(draft)}`);
          setSaveStatus('saved');
        } catch (err) {
          console.error('[MarkCard] text save failed:', err);
          setSaveStatus('error');
        }
      } else {
        console.error('[MarkCard] promote failed — no ID returned');
        setSaveStatus('error');
      }
      setTimeout(() => setSaveStatus(null), 2000);
      onRefresh();  // need full refresh to get the new mark ID
      return;
    }
    // Save with feedback
    setSaveStatus('saving');
    axios.put(`${BACKEND_URL}/marks/${m.id}/text?text=${encodeURIComponent(draft)}`)
      .then(() => { dirtyRef.current = false; setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); })
      .catch(err => { console.error('[MarkCard] text save failed:', err); setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 4000); });
  };

  const handleTranslationBlur = async (languageKey) => {
    if (isVirtual) {
      setSaveStatus('saving');
      const newId = await promoteToMark();
      if (newId) {
        try {
          await axios.put(`${BACKEND_URL}/marks/${newId}/translation`, {
            language: languageKey,
            text: translationDrafts[languageKey] || '',
          });
          setSaveStatus('saved');
        } catch (err) {
          console.error('[MarkCard] translation save failed:', err);
          setSaveStatus('error');
        }
      } else {
        setSaveStatus('error');
      }
      dirtyRef.current = false;
      setTimeout(() => setSaveStatus(null), 2000);
      onRefresh();  // need full refresh to get the new mark ID
      return;
    }
    // Save with feedback
    setSaveStatus('saving');
    axios.put(`${BACKEND_URL}/marks/${m.id}/translation`, {
      language: languageKey,
      text: translationDrafts[languageKey] || '',
    })
      .then(() => { dirtyRef.current = false; setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); })
      .catch(err => { console.error('[MarkCard] translation save failed:', err); setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 4000); });
  };

  const nudge = (field, delta) => {
    if (field === 'start') {
      setAdjStart(v => Math.max(0, Math.round((v + delta) * 1000) / 1000));
    } else {
      setAdjEnd(v => Math.max(0, Math.round((v + delta) * 1000) / 1000));
    }
  };

  const saveTimecode = async () => {
    setTcSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/marks/${m.id}/timecode?adjusted_start=${adjStart}&adjusted_end=${adjEnd}`);
      // Reload subtitle track so player reflects the new timing immediately
      if (videoRef?.current) {
        const video = videoRef.current;
        const currentTime = video.currentTime;
        const paused = video.paused;
        // Force track reload by toggling mode
        for (let i = 0; i < video.textTracks.length; i++) {
          const track = video.textTracks[i];
          const prev = track.mode;
          track.mode = 'disabled';
          track.mode = prev;
        }
        video.currentTime = currentTime;
        if (!paused) video.play().catch(() => {});
      }
      onRefresh();
      setTcOpen(false);
    } catch (err) {
      console.error('[MarkCard] timecode save failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setTcSaving(false);
    }
  };

  const resetTimecode = async () => {
    setTcSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/marks/${m.id}/timecode?adjusted_start=-1&adjusted_end=-1`);
      setAdjStart(m.timestamp_start);
      setAdjEnd(origEnd);
      onRefresh();
      setTcOpen(false);
    } catch (err) {
      console.error('[MarkCard] timecode reset failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setTcSaving(false);
    }
  };

  const fmtInput = (s) => {
    // Format float seconds as HH:MM:SS.mmm for the input display
    s = Math.max(0, s);
    const h  = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const sc = (s % 60).toFixed(3);
    return `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}:${sc.padStart(6,'0')}`;
  };

  const parseInput = (str) => {
    // Parse HH:MM:SS.mmm back to float seconds
    const parts = str.split(':');
    if (parts.length === 3) {
      return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseFloat(parts[2]);
    }
    return parseFloat(str) || 0;
  };

  const tcAccent = isAdjusted ? '#ff9f0a' : theme.accent;
  // Word-level split data for this cue (present only for "straddling" cues where
  // two speakers meet inside one cue). Keyed by the cue's original start, 2dp.
  const wordCue = cueWords[(m.timestamp_start ?? 0).toFixed(2)];
  const isStraddling = !!(wordCue && wordCue.straddles);
  const sourceLanguageLabel = sourceLanguage || editingLanguage || 'Original';
  const sourceIsRTL = isRightToLeftLanguage(sourceLanguageLabel);
  // Testimony reads in the reading serif (falls back to chrome font, then
  // inherit); Hebrew/Arabic keep their own script fonts via the helper.
  const sourceFontFamily = getLanguageFontFamily(sourceLanguageLabel, theme.readingFont || theme.fontFamily || 'inherit');
  const labelColumnWidth = '90px';
  const renderLanguagePill = (language, isEditor = false, icon = null) => {
    const meta = getLanguageMeta(language);
    const code = getLanguageCode(language);
    return (
      <span
        title={meta.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          padding: '2px 7px',
          borderRadius: '999px',
          border: `1px solid ${meta.border.replace('0.34', '0.55').replace('0.3)', '0.55)')}`,
          background: meta.bg.replace('0.14', '0.22').replace('0.12', '0.22'),
          color: meta.text,
          fontSize: '0.58rem',
          fontWeight: 700,
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          textTransform: 'uppercase',
        }}
      >
        {icon && <Icon icon={icon} size={10} color={meta.text} />}
        {code}
        {isEditor && <Icon icon={PenLine} size={10} color={meta.text} />}
      </span>
    );
  };

  const renderMetaPill = (label) => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 7px',
        borderRadius: '999px',
        border: '1px solid #64748b55',
        background: 'rgba(100,116,139,0.16)',
        color: theme.textSub,
        fontSize: '0.58rem',
        fontWeight: 700,
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  );

  const editableLanguageRows = React.useMemo(() => {
    const visibleKeys = [...new Set(
      visibleLanguages
        .map(language => normalizeLanguageKey(language))
        .filter(Boolean)
    )];
    const editingKey = normalizeLanguageKey(editingLanguage);
    // Deduplicated ordered keys: editing language first, then others
    const orderedKeys = [...new Set([
      ...(editingKey ? [editingKey] : []),
      ...visibleKeys,
    ])];

    return orderedKeys.map((languageKey) => {
      const isEditing = languageKey === editingKey;
      // Only use draft for a VTT language that differs from the interview language
      const isVttLanguage = vttLangKey && languageKey === vttLangKey;
      const isRTL = isRightToLeftLanguage(languageKey);
      const fontFamily = getLanguageFontFamily(languageKey, theme.readingFont || theme.fontFamily || 'inherit');
      return {
        key: languageKey,
        label: getLanguageMeta(languageKey).label,
        isSourceLanguage: isVttLanguage,
        isEditing,
        isRTL,
        fontFamily,
        value: isVttLanguage ? draft : (translationDrafts[languageKey] || ''),
        onChange: (value) => {
          dirtyRef.current = true;
          if (isVttLanguage) {
            setDraft(value);
          } else {
            setTranslationDrafts(prev => ({ ...prev, [languageKey]: value }));
          }
        },
        onBlur: isVttLanguage ? handleBlur : () => handleTranslationBlur(languageKey),
      };
    });
  }, [draft, editingLanguage, handleBlur, vttLangKey, translationDrafts, visibleLanguages]);

  // Seek the player to this mark (shared by the timecode pill's click and keyboard activation)
  //
  // play() returns a promise that REJECTS if the element is paused or reloaded
  // before playback starts — seeking, or React swapping the subtitle <track>,
  // both do that. The rejection is benign and expected, but unhandled it
  // surfaced as "AbortError: The play() request was interrupted by a call to
  // pause()" in the console every time a timecode was clicked. Swallow only
  // that; anything else still reaches the console.
  const safePlay = () => {
    const pr = videoRef?.current?.play?.();
    if (pr && typeof pr.catch === 'function') {
      pr.catch(err => { if (err?.name !== 'AbortError') throw err; });
    }
  };

  const seekToMark = () => {
    const cardPart = m.part_number;
    if (cardPart && selectedPart && cardPart !== selectedPart) {
      // Switch to the correct tape first, then seek after load
      onSwitchTape(cardPart);
      setTimeout(() => {
        if (videoRef?.current) { videoRef.current.currentTime = adjStart; safePlay(); }
      }, 500);
    } else if (videoRef?.current) {
      videoRef.current.currentTime = adjStart;
      safePlay();
    }
  };

  return (
    <div style={{ background: m.status === 'completed' ? theme.cardDone : m.flagged ? '#f59e0b08' : theme.card, borderRadius: '10px', marginBottom: '10px', border: `1px solid ${isActive ? (theme.accent || '#3b82f6') + '88' : m.flagged ? '#f59e0b44' : isAdjusted ? '#ff9f0a55' : rangeRestricted ? '#94a3b833' : theme.border}`, transition: '0.3s', overflow: 'hidden', boxShadow: isActive ? `0 0 0 1px ${theme.accent || '#3b82f6'}44` : 'none', opacity: rangeRestricted ? 0.55 : 1 }}>

      {/* Range restricted indicator */}
      {rangeRestricted && (
        <div style={{ padding: '3px 10px', background: 'rgba(148,163,184,0.08)', borderBottom: '1px solid rgba(148,163,184,0.15)', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8' }}>
          <Icon icon={Lock} size={8} /> Outside your assigned range
        </div>
      )}

      {/* ── Main card body ── */}
      <div style={{ padding: theme.fsCard, fontSize: theme.fsCardText }}>
        {/* Row 1: Timecode + Tape number + Assigned pill (flush right) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            {/* Timecode pill — click to seek, Clock icon to open editor */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
              <span
                onClick={seekToMark}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); seekToMark(); } }}
                style={{ color: tcAccent, fontWeight: 'bold', cursor: 'pointer', fontFamily: 'monospace', fontSize: theme.fsTimestamp,
                         background: isAdjusted ? '#ff9f0a18' : 'transparent',
                         border: isAdjusted ? '1px solid #ff9f0a44' : '1px solid transparent',
                         borderRight: 'none', borderRadius: '6px 0 0 6px', padding: '1px 6px' }}
                title={isAdjusted ? `Original: ${formatVTTTime(m.timestamp_start)}` : `Click to seek${m.part_number !== selectedPart ? ` (switches to Tape ${m.part_number})` : ''}`}
              >
                {formatVTTTime(adjStart)}
              </span>
              {canEdit && <button
                onClick={() => setTcOpen(o => !o)}
                title="Edit timecode"
                style={{ background: isAdjusted ? '#ff9f0a18' : theme.card,
                         border: isAdjusted ? '1px solid #ff9f0a44' : `1px solid ${theme.border}`,
                         borderLeft: 'none', borderRadius: '0 6px 6px 0',
                         color: isAdjusted ? '#ff9f0a' : theme.textSub,
                         cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '1px 5px' }}
              >
                <Clock size={11} strokeWidth={2} />
              </button>}
            </div>

            {/* Tape pill — always shown */}
            <div style={{ background: theme.card, color: theme.textSub, padding: '2px 7px', borderRadius: '10px', border: `1px solid ${theme.border}`, fontSize: theme.fsBadge, fontWeight: 'bold', fontFamily: 'monospace' }}>
              T{m.part_number}
            </div>

            {/* Straddling cue: two speakers share this cue. Show a "2 voices" flag
                (with both speaker colours) instead of a single misleading pill;
                the word row below is where the boundary word gets pulled. */}
            {isStraddling && (() => {
              const spks = Object.values(speakerMap).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
              const c0 = spks[0]?.color || '#6b7280';
              const c1 = spks[1]?.color || '#6b7280';
              return (
                <div
                  title="Two speakers share this cue — use the word row below to move a word to the right speaker"
                  style={{
                    background: theme.card, color: theme.textSub, padding: '2px 8px', borderRadius: '10px',
                    border: `1px solid ${theme.border}`, fontSize: theme.fsBadge, fontWeight: 'bold',
                    display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ display: 'flex', gap: '2px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c0, display: 'inline-block' }} />
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c1, display: 'inline-block' }} />
                  </span>
                  <Icon icon={Users} size={11} style={{ marginRight: '1px' }} />2 voices
                </div>
              );
            })()}

            {/* Speaker pill — only when the cue has a known single speaker (diarization). Click to reassign.
                Carries the compare overlay: when the second engine disagrees the pill picks up an amber
                ring, so the cues worth a human ear are visible while scrolling rather than needing a
                separate list. In compare mode the pill shows the second engine's answer instead. */}
            {!isStraddling && m.speaker && speakerMap[m.speaker] && (() => {
              const disagrees = !!m.speaker_alt && m.speaker_alt !== m.speaker;
              // Compare mode only means anything where there IS a second opinion.
              const shownCluster = (speakerCompareMode && m.speaker_alt) ? m.speaker_alt : m.speaker;
              const spk = speakerMap[shownCluster] || speakerMap[m.speaker];
              const col = spk.color || theme.accent || '#6b7280';
              const spkList = Object.values(speakerMap).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
              const curIdx = spkList.findIndex(s => s.cluster_id === m.speaker);
              const nextCluster = spkList.length > 1 ? spkList[(curIdx + 1) % spkList.length].cluster_id : null;
              // Never editable while showing the other engine's answer — a click
              // would reassign based on a label the reader is not looking at.
              const canReassign = canEdit && onReassignSpeaker && nextCluster && !speakerCompareMode;
              const altLabel = m.speaker_alt ? (speakerMap[m.speaker_alt]?.label || m.speaker_alt) : '';
              const primaryLabel = speakerMap[m.speaker]?.label || m.speaker;
              const tip = speakerCompareMode
                ? `Compare engine says ${spk.label || shownCluster}${disagrees ? ` — the primary says ${primaryLabel}` : ' (agrees)'}`
                : disagrees
                  ? `Speaker: ${primaryLabel}. The compare engine says ${altLabel} — worth a listen.`
                  : canReassign
                    ? `Speaker: ${primaryLabel} — click to reassign to ${speakerMap[nextCluster]?.label || nextCluster}`
                    : `Speaker: ${primaryLabel}`;
              return (
                <div
                  onClick={canReassign ? () => onReassignSpeaker(m, nextCluster) : undefined}
                  title={tip}
                  style={{
                    background: col + '1a', color: col, padding: '2px 8px', borderRadius: '10px',
                    border: disagrees ? '1px solid #f59e0b' : `1px solid ${col}55`,
                    boxShadow: disagrees ? '0 0 0 2px #f59e0b22' : 'none',
                    fontSize: theme.fsBadge, fontWeight: 'bold',
                    display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap',
                    cursor: canReassign ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: col, display: 'inline-block', flexShrink: 0 }} />
                  {spk.label || shownCluster}
                  {disagrees && (
                    /* Not colour alone: the ring is reinforced by a mark, so the
                       flag survives a colour-blind reader and a greyscale print. */
                    <span aria-label="engines disagree" style={{ color: '#f59e0b', fontWeight: 900, lineHeight: 1 }}>?</span>
                  )}
                </div>
              );
            })()}

            {/* Save status indicator */}
            {saveStatus && (
              <Badge
                theme={theme}
                uppercase={false}
                status={saveStatus === 'saved' ? 'success' : saveStatus === 'error' ? 'danger' : 'info'}
                style={{ transition: 'opacity 0.3s' }}
              >
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save failed!'}
              </Badge>
            )}

            {/* Assigned pill rendered inline with assignment note below */}
        </div>

        {/* Word-split strip — only for straddling cues. Each word is coloured by
            its assigned speaker; clicking a word moves it to the other speaker.
            Read-only when the user cannot edit. */}
        {isStraddling && (
          <div style={{
            marginBottom: '8px', padding: '7px 9px', borderRadius: '8px',
            background: theme.sidebar || theme.card, border: `1px dashed ${theme.border}`,
          }}>
            <div style={{ fontSize: theme.fsBadge, color: theme.textSub, marginBottom: '5px', fontWeight: 600 }}>
              {canEdit && onReassignWord
                ? 'Two speakers in this cue — click a word to move it to the other speaker.'
                : 'Two speakers in this cue.'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', lineHeight: 1.9 }}>
              {[...wordCue.words].sort((a, b) => a.index - b.index).map((w) => {
                const spk = speakerMap[w.speaker];
                const col = spk?.color || theme.textSub || '#6b7280';
                const spkList = Object.values(speakerMap).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
                const curIdx = spkList.findIndex(s => s.cluster_id === w.speaker);
                const nextCluster = spkList.length > 1 ? spkList[(curIdx + 1) % spkList.length].cluster_id : null;
                const canMove = canEdit && onReassignWord && nextCluster;
                return (
                  <span
                    key={w.index}
                    onClick={canMove ? () => onReassignWord(m, w.index, nextCluster) : undefined}
                    title={canMove
                      ? `${w.text} — ${spk?.label || w.speaker}. Click to move to ${speakerMap[nextCluster]?.label || nextCluster}.`
                      : `${spk?.label || w.speaker}`}
                    style={{
                      background: col + '22', color: col, padding: '1px 6px', borderRadius: '5px',
                      border: `1px solid ${col}55`, fontSize: theme.fsBadge, fontWeight: 600,
                      cursor: canMove ? 'pointer' : 'default', whiteSpace: 'nowrap',
                    }}
                  >
                    {w.text}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Row 2: Status pills + Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {isAdjusted && (
              <span style={{ fontSize: theme.fsBadge, color: '#ff9f0a', fontWeight: 'bold', fontFamily: 'monospace', opacity: 0.8 }}
                    title={`Original: ${formatVTTTime(m.timestamp_start)}`}>
                TC
              </span>
            )}
            {/* Edit status pill — AI (unedited) or MANUAL (human-edited) */}
            {(() => {
              const isEdited = m.fix_type === 'manual' || (m.edited_text && m.edited_text.trim() !== m.original_text?.trim());
              const isAutoFixed = m.fix_type === 'auto';
              if (isAutoFixed) return (
                <div style={{ background: theme.card, color: theme.textSub, padding: '2px 8px', borderRadius: '12px', border: `1px solid ${theme.border}`, fontSize: theme.fsBadge, fontWeight: 'bold' }}>
                  <Icon icon={Wand2} size={11} style={{marginRight:'3px'}} />SMART FIX
                </div>
              );
              if (isEdited) return (
                <div style={{ position: 'relative' }}
                  onMouseEnter={showEditHistory} onMouseLeave={hideEditHistory}>
                  <div style={{ background: '#007aff22', color: '#007aff', padding: '2px 8px', borderRadius: '12px', border: '1px solid #007aff33', fontSize: theme.fsBadge, fontWeight: 'bold', cursor: 'default' }}>
                    <Icon icon={PenLine} size={11} style={{marginRight:'3px'}} />EDITED
                  </div>
                  {editTooltip && (
                    <div onMouseEnter={keepEditHistory} onMouseLeave={hideEditHistory}
                      style={{ position: 'absolute', top: '100%', left: 0, zIndex: Z.dropdown, marginTop: '4px', minWidth: '280px', maxWidth: '380px', maxHeight: '240px', overflowY: 'auto', background: theme.sidebar || theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: '8px', fontSize: '0.65rem' }}>
                      {editTooltip.loading ? (
                        <div style={{ color: theme.textSub, padding: '8px', textAlign: 'center' }}>Loading history…</div>
                      ) : editTooltip.edits.length === 0 ? (
                        <div style={{ color: theme.textSub, padding: '8px', textAlign: 'center' }}>No edit history recorded</div>
                      ) : editTooltip.edits.map((e, i) => {
                        // Resolve assigned_to IDs to editor names
                        const isAssignment = e.field === 'assigned_to';
                        const resolveVal = (v) => {
                          if (!isAssignment) return v;
                          if (!v || v === '' || v === 'null') return null;
                          const id = parseInt(v, 10);
                          if (isNaN(id)) return v;
                          const ed = editors.find(x => x.id === id);
                          return ed ? ed.name : `User #${id}`;
                        };
                        const oldDisplay = resolveVal(e.old_value);
                        const newDisplay = resolveVal(e.new_value);
                        const fieldLabel = e.field?.replace('translation.', '').replace('assigned_to', 'ASSIGNED TO').replace('edited_text', 'TEXT').toUpperCase();
                        return (
                        <div key={e.id} style={{ padding: '5px 4px', borderBottom: i < editTooltip.edits.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '3px' }}>
                            <span style={{ fontWeight: 700, color: theme.textSub }}>{fieldLabel}</span>
                            {e.action === 'revert' && <span style={{ fontSize: '0.55rem', color: '#8b5cf6', fontWeight: 700 }}>RESTORED</span>}
                            <span style={{ marginLeft: 'auto', fontSize: '0.55rem', color: theme.textSub }}>by {e.user_name || 'unknown'} · {e.created_at ? new Date(e.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </div>
                          <div style={{ color: '#ef4444', textDecoration: 'line-through', opacity: 0.6, fontSize: '0.6rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
                            {oldDisplay ? (oldDisplay.length > 100 ? oldDisplay.slice(0, 100) + '…' : oldDisplay) : '(empty)'}
                          </div>
                          <div style={{ color: theme.success || '#34c759', fontSize: '0.6rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
                            {newDisplay ? (newDisplay.length > 100 ? newDisplay.slice(0, 100) + '…' : newDisplay) : '(empty)'}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
              return (
                <div style={{ background: theme.card, color: theme.textSub, padding: '2px 8px', borderRadius: '12px', border: `1px solid ${theme.border}`, fontSize: theme.fsBadge, fontWeight: 'bold', opacity: 0.6 }}>
                  AI
                </div>
              );
            })()}
            {/* Flagged pill */}
            {m.flagged && (
              <div style={{ background: '#f59e0b22', color: '#f59e0b', padding: '2px 8px', borderRadius: '12px', border: '1px solid #f59e0b33', fontSize: theme.fsBadge, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Icon icon={Flag} size={10} />FLAGGED
              </div>
            )}
            {/* Assigned pill moved to Row 1 */}
          </div>
          {/* Action buttons — flag, review, delete (editors/admins only) */}
          {canEdit && <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {/* Flag / Unflag */}
            {m.id && (
              <button
                onClick={async () => {
                  if (m.flagged) {
                    axios.put(`${BACKEND_URL}/marks/${m.id}/unflag`).then(onRefresh);
                  } else {
                    // Reason is REQUIRED here (owner decision, 2026-07-19): a
                    // flag travels to whoever picks the work up next, and one
                    // with no reason costs them the context. The two assignment
                    // notes below stay optional — those go to a named person
                    // who already has the surrounding conversation.
                    const reason = await prompt({
                      title: 'Flag for review',
                      message: 'Why is this being flagged? The next reviewer will see this.',
                      placeholder: 'e.g. name spelling uncertain',
                      confirmLabel: 'Flag',
                    });
                    if (reason == null) return;   // Cancel must not flag
                    axios.put(`${BACKEND_URL}/marks/${m.id}/flag?reason=${encodeURIComponent(reason)}`).then(onRefresh);
                  }
                }}
                style={{
                  background: 'none', border: `1px solid ${m.flagged ? '#f59e0b44' : theme.border}`,
                  borderRadius: '5px', padding: '2px 6px', cursor: 'pointer',
                  color: m.flagged ? '#f59e0b' : theme.textSub,
                  display: 'flex', alignItems: 'center', gap: '3px', fontSize: theme.fsBadge, fontWeight: 600,
                }}
              >
                <Icon icon={Flag} size={10} />{m.flagged ? 'Unflag' : 'Flag'}
              </button>
            )}
            {/* Assign */}
            {m.id && editors.length > 0 && (
              m.assigned_to ? (
                <button
                  onClick={() => axios.put(`${BACKEND_URL}/marks/${m.id}/unassign`).then(onRefresh)}
                  title={`Assigned${m.assignment_note ? ': ' + m.assignment_note : ''}`}
                  style={{
                    background: '#7c3aed14', border: '1px solid #7c3aed44',
                    borderRadius: '5px', padding: '2px 6px', cursor: 'pointer',
                    color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '3px',
                    fontSize: theme.fsBadge, fontWeight: 600,
                  }}
                >
                  <Icon icon={Users} size={10} />
                  {(editors.find(e => e.id === m.assigned_to) || {}).name || '?'}
                  <span style={{ fontSize: '0.5rem', opacity: 0.7 }}>×</span>
                </button>
              ) : (
                <select
                  value=""
                  onChange={async e => {
                    const uid = parseInt(e.target.value);
                    if (!uid) return;
                    const note = await prompt({
                      title: 'Assign for review',
                      message: 'Assignment note (optional):',
                      confirmLabel: 'Assign',
                      allowEmpty: true,
                    });
                    if (note == null) return;   // Cancel must not assign
                    // Assign for review
                    axios.put(`${BACKEND_URL}/marks/${m.id}/assign?user_id=${uid}&note=${encodeURIComponent(note)}`).then(onRefresh);
                    // Also grant edit access via task assignment (admin only)
                    if (userRole === 'admin' && projectRoot) {
                      const ts = m.timestamp_start ?? m.start ?? 0;
                      const te = m.timestamp_end ?? m.end ?? (ts + 10);
                      axios.post(`${BACKEND_URL}/admin/task-assignments`, {
                        user_id: uid,
                        project_root: projectRoot,
                        part_number: m.part_number || selectedPart,
                        tc_start: Math.max(0, ts - 0.5),
                        tc_end: te + 0.5,
                        note: note || null,
                      }).catch(() => {}); // silently — edit access is a bonus
                    }
                  }}
                  style={{
                    background: 'none', border: `1px solid ${theme.border}`,
                    borderRadius: '5px', padding: '2px 4px', cursor: 'pointer',
                    color: theme.textSub, fontSize: theme.fsBadge, fontWeight: 600,
                  }}
                >
                  <option value="">Assign…</option>
                  {editors.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              )
            )}
            {/* Grant Access — for virtual blocks or when no mark-level assign exists */}
            {!m.id && userRole === 'admin' && projectRoot && editors.length > 0 && (
              <select
                value=""
                onChange={async e => {
                  const uid = parseInt(e.target.value);
                  if (!uid) return;
                  const note = await prompt({
                    title: 'Grant edit access',
                    message: 'Assignment note (optional):',
                    confirmLabel: 'Grant access',
                    allowEmpty: true,
                  });
                  if (note == null) return;   // Cancel must not grant access
                  const ts = m.timestamp_start ?? m.start ?? 0;
                  const te = m.timestamp_end ?? m.end ?? (ts + 10);
                  axios.post(`${BACKEND_URL}/admin/task-assignments`, {
                    user_id: uid,
                    project_root: projectRoot,
                    part_number: m.part_number || selectedPart,
                    tc_start: Math.max(0, ts - 0.5),
                    tc_end: te + 0.5,
                    note: note || null,
                  }).then(onRefresh).catch(() => {});
                }}
                style={{
                  background: 'none', border: `1px solid ${theme.border}`,
                  borderRadius: '5px', padding: '2px 4px', cursor: 'pointer',
                  color: theme.textSub, fontSize: theme.fsBadge, fontWeight: 600,
                }}
              >
                <option value="">Assign…</option>
                {editors.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            )}
            {/* Approve — moves a pending mark to completed (reviewed) */}
            {m.id && m.status === 'pending' && (
              <button
                onClick={() => {
                  axios.put(`${BACKEND_URL}/marks/${m.id}/review`).then(onRefresh);
                }}
                title="Approve this correction (moves to Reviewed)"
                style={{
                  background: '#34c75914', border: '1px solid #34c75944',
                  borderRadius: '5px', padding: '2px 6px', cursor: 'pointer',
                  color: '#34c759',
                  display: 'flex', alignItems: 'center', gap: '3px',
                  fontSize: theme.fsBadge, fontWeight: 600,
                }}
              >
                <Icon icon={Check} size={10} />Approve
              </button>
            )}
            {/* Delete */}
            {!isVirtual && m.id && (
              <Button
                theme={theme}
                variant="danger-ghost"
                size="sm"
                icon={Trash2}
                onClick={async () => {
                  if (!await confirm({
                    message: 'Delete this correction permanently? This cannot be undone.',
                    title: 'Delete correction?',
                    confirmLabel: 'Delete',
                    destructive: true,
                  })) return;
                  axios.delete(`${BACKEND_URL}/marks/${m.id}`).then(onRefresh);
                }}
              >Delete</Button>
            )}
          </div>}
        </div>

        {/* Reviewed-by stamp */}
        {m.status === 'completed' && m.reviewed_by_name && (
          <div style={{
            padding: '4px 10px', borderRadius: '6px', marginBottom: '6px',
            background: '#34c75910', border: '1px solid #34c75933',
            fontSize: theme.fsBadge, color: '#34c759', lineHeight: 1.4,
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <Icon icon={Check} size={11} style={{ flexShrink: 0 }} />
            <span>Reviewed by <strong>{m.reviewed_by_name}</strong>{m.reviewed_at ? ` · ${new Date(m.reviewed_at).toLocaleDateString()}` : ''}</span>
          </div>
        )}

        {/* Flag reason */}
        {m.flagged && m.flag_reason && (
          <div style={{
            padding: '6px 10px', borderRadius: '6px', marginBottom: '8px',
            background: '#f59e0b0a', border: '1px solid #f59e0b22',
            fontSize: theme.fsBadge, color: '#f59e0b', lineHeight: 1.4,
            display: 'flex', alignItems: 'flex-start', gap: '6px',
          }}>
            <Icon icon={Flag} size={11} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>{m.flag_reason}</span>
          </div>
        )}

        {/* QA suggestion */}
        {m.qa_suggestion && (
          <div style={{
            padding: '8px 10px', borderRadius: '6px', marginBottom: '8px',
            background: '#f472b60a', border: '1px solid #f472b622',
            fontSize: theme.fsBadge, lineHeight: 1.5,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
              <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#f472b6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                QA Suggestion{m.qa_issue_type ? ` — ${m.qa_issue_type.replace(/_/g, ' ')}` : ''}
              </span>
            </div>
            <div style={{ color: '#f472b6', fontStyle: 'italic' }}>{m.qa_suggestion}</div>
            {canEdit && (
              <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                <Button theme={theme} variant="success" size="sm" onClick={async () => {
                  if (!await confirm({
                    message: 'Accept QA suggestion and apply as edited text?',
                    title: 'Accept QA suggestion?',
                    confirmLabel: 'Accept',
                    destructive: false,
                  })) return;
                  axios.put(`${BACKEND_URL}/marks/${m.id}/accept-qa`)
                    .then(() => { if (onRefresh) onRefresh(); })
                    .catch(() => {});
                }}>
                  Accept
                </Button>
                <Button theme={theme} variant="secondary" size="sm" onClick={() => {
                  axios.put(`${BACKEND_URL}/marks/${m.id}/dismiss-qa`)
                    .then(() => { if (onRefresh) onRefresh(); })
                    .catch(() => {});
                }}>
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Assignment pill + note */}
        {m.assigned_to && (
          <div style={{
            padding: '5px 10px', borderRadius: '6px', marginBottom: '8px',
            background: m.assigned_to === currentUserId ? '#0ea5e908' : '#7c3aed08',
            border: `1px solid ${m.assigned_to === currentUserId ? '#0ea5e918' : '#7c3aed18'}`,
            fontSize: theme.fsBadge, lineHeight: 1.4,
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 7px', borderRadius: '10px', fontWeight: 'bold', flexShrink: 0,
              background: m.assigned_to === currentUserId ? '#0ea5e914' : '#7c3aed14',
              color: m.assigned_to === currentUserId ? '#0ea5e9' : '#7c3aed',
              border: `1px solid ${m.assigned_to === currentUserId ? '#0ea5e933' : '#7c3aed33'}`,
            }}>
              <Icon icon={Users} size={10} />
              {m.assigned_to === currentUserId ? 'Yours' : (editors.find(e => e.id === m.assigned_to) || {}).name || 'Assigned'}
            </span>
            {m.assignment_note && <span style={{ color: m.assigned_to === currentUserId ? '#0ea5e9' : '#7c3aed', opacity: 0.8 }}>{m.assignment_note}</span>}
          </div>
        )}

        {/* AI Original — interview language source text */}
        {(() => {
          // Show the interview language text: use translation if available, otherwise fall back to original_text
          const interviewText = (m.translations && m.translations[sourceLanguageKey])
            ? m.translations[sourceLanguageKey]
            : m.original_text;
          return (
            <div style={{ marginBottom: '8px', padding: '6px 8px', borderRadius: '6px', background: theme.sidebar, border: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                {renderMetaPill('AI')}
                <span style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Interview Language ({sourceLanguageLabel})
                </span>
                {canEdit && (draft.trim() !== (m.original_text || '').trim()) && (
                  <Button
                    theme={theme}
                    variant="ghost"
                    size="sm"
                    icon={RotateCcw}
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                      dirtyRef.current = false;
                      setDraft(m.original_text || '');
                      if (m.id) {
                        axios.put(`${BACKEND_URL}/marks/${m.id}/text?text=${encodeURIComponent(m.original_text)}`).then(onRefresh);
                      }
                    }}
                  >Revert</Button>
                )}
              </div>
              <div
                dir={sourceIsRTL ? 'rtl' : 'ltr'}
                style={{
                  fontSize: theme.fsOriginalText,
                  color: theme.textSub,
                  fontStyle: 'italic',
                  textAlign: sourceIsRTL ? 'right' : 'left',
                  fontFamily: sourceFontFamily,
                  lineHeight: 1.5,
                }}
              >
                {interviewText}
              </div>
            </div>
          );
        })()}

        {/* Editable text by language — all pills aligned vertically */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {editableLanguageRows.map(row => (
            <div key={row.key} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '6px' }}>
              <div style={{ width: '52px', minWidth: '52px', display: 'flex', justifyContent: row.isRTL ? 'flex-end' : 'flex-start', flexShrink: 0, paddingTop: '6px' }}>
                {renderLanguagePill(row.label, row.isEditing)}
              </div>
              <textarea
                dir={row.isRTL ? 'rtl' : 'ltr'}
                value={row.value}
                onChange={canEdit ? e => row.onChange(e.target.value) : undefined}
                onBlur={canEdit ? row.onBlur : undefined}
                readOnly={!canEdit}
                placeholder={canEdit ? `Edit ${row.label}` : row.label}
                style={{
                  ...inputStyle,
                  minHeight: '50px',
                  fontSize: theme.fsEditorText,
                  flex: 1,
                  textAlign: row.isRTL ? 'right' : 'left',
                  direction: row.isRTL ? 'rtl' : 'ltr',
                  fontFamily: row.fontFamily,
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Inline timecode editor — slides in below card body ── */}
      {tcOpen && (
        <div style={{
          borderTop: `1px solid ${'#ff9f0a'}44`,
          background: '#ff9f0a08',
          padding: '12px 14px',
        }}>
          {/* Nudge selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.65rem', color: theme.textSub, fontWeight: 'bold', letterSpacing: '0.05em' }}>NUDGE</span>
            {NUDGES.map(n => (
              <button key={n.v} onClick={() => setActiveNudge(n.v)} style={{
                padding: '2px 8px', fontSize: '0.7rem', fontWeight: 'bold', border: 'none', borderRadius: '4px', cursor: 'pointer',
                background: activeNudge === n.v ? '#ff9f0a' : theme.card,
                color:      activeNudge === n.v ? '#fff'    : theme.textSub,
              }}>{n.label}</button>
            ))}
          </div>

          {/* START row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <span style={{ width: '38px', fontSize: '0.65rem', fontWeight: 'bold', color: theme.textSub, letterSpacing: '0.05em' }}>START</span>
            <button onClick={() => nudge('start', -activeNudge)} style={{ padding: '3px 8px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.text, fontWeight: 'bold' }}>−</button>
            <input
              value={fmtInput(adjStart)}
              onChange={e => { const v = parseInput(e.target.value); if (!isNaN(v)) setAdjStart(Math.max(0, v)); }}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold', color: '#ff9f0a',
                       background: theme.inputBg, border: `1px solid #ff9f0a44`, borderRadius: '4px',
                       padding: '3px 6px', width: '110px', textAlign: 'center' }}
            />
            <button onClick={() => nudge('start', +activeNudge)} style={{ padding: '3px 8px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.text, fontWeight: 'bold' }}>+</button>
            <span style={{ fontSize: '0.65rem', color: theme.textSub, marginLeft: '4px' }}>
              orig: <span style={{ fontFamily: 'monospace' }}>{formatVTTTime(m.timestamp_start)}</span>
            </span>
          </div>

          {/* END row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <span style={{ width: '38px', fontSize: '0.65rem', fontWeight: 'bold', color: theme.textSub, letterSpacing: '0.05em' }}>END</span>
            <button onClick={() => nudge('end', -activeNudge)} style={{ padding: '3px 8px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.text, fontWeight: 'bold' }}>−</button>
            <input
              value={fmtInput(adjEnd)}
              onChange={e => { const v = parseInput(e.target.value); if (!isNaN(v)) setAdjEnd(Math.max(0, v)); }}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold', color: '#ff9f0a',
                       background: theme.inputBg, border: `1px solid #ff9f0a44`, borderRadius: '4px',
                       padding: '3px 6px', width: '110px', textAlign: 'center' }}
            />
            <button onClick={() => nudge('end', +activeNudge)} style={{ padding: '3px 8px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.text, fontWeight: 'bold' }}>+</button>
            <span style={{ fontSize: '0.65rem', color: theme.textSub, marginLeft: '4px' }}>
              orig: <span style={{ fontFamily: 'monospace' }}>{formatVTTTime(origEnd)}</span>
            </span>
          </div>

          {/* Action row */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={saveTimecode} disabled={tcSaving} style={{
              padding: '5px 14px', background: '#ff9f0a', color: '#fff', border: 'none',
              borderRadius: '6px', fontWeight: 'bold', fontSize: '0.75rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '5px', opacity: tcSaving ? 0.6 : 1,
            }}>
              <Check size={12} />{tcSaving ? 'Saving…' : 'Save'}
            </button>
            {isAdjusted && (
              <button onClick={resetTimecode} disabled={tcSaving} style={{
                padding: '5px 14px', background: 'none', color: theme.textSub,
                border: `1px solid ${theme.border}`, borderRadius: '6px',
                fontWeight: 'bold', fontSize: '0.75rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '5px',
              }}>
                <RotateCcw size={11} />Reset to original
              </button>
            )}
            <button onClick={() => setTcOpen(false)} style={{
              padding: '5px 10px', background: 'none', color: theme.textSub,
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem',
            }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
});

export default MarkCard;
